import {
  Button,
  Input,
  Search,
  Users,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { Pagination } from "../../components/molecules/Pagination";
import {
  cancelInvitationRequest,
  clearInvitationError,
  resendInvitationRequest,
  selectCancelLoading,
  selectInvitationsError,
  selectResendLoading,
} from "../../store/reducers/doctorInvitationSlice";
import {
  bulkImportStaticDoctorProfilesRequest,
  clearStaticDoctorProfileError,
  fetchStaticDoctorProfilesRequest,
  inviteFromProfileRequest,
  selectBulkImportLoading,
  selectInviteLoadingId,
  selectStaticDoctorProfiles,
  selectStaticDoctorProfilesError,
  selectStaticDoctorProfilesLoading,
} from "../../store/reducers/staticDoctorProfileSlice";
import { staticDoctorProfileApi } from "../../services/staticDoctorProfile.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { BulkRowError } from "../../types/doctorInvitation.definition";
import {
  BulkStaticDoctorProfileImportRequest,
  StaticDoctorProfileDto,
  StaticDoctorProfileStatus,
} from "../../types/staticDoctorProfile.definition";

const STATUS_BADGE_CLASS: Record<StaticDoctorProfileStatus, string> = {
  ACTIVE:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700",
  INVITED:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800",
  REPLACED:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800",
  ARCHIVED:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500",
};

type ParsedRow = {
  firstName: string;
  lastName: string;
  civility: string;
  phoneNumber: string;
  specialtyCodes: string[];
  facilityName: string;
  city: string;
  state: string;
};

const CSV_HEADERS = [
  "firstName",
  "lastName",
  "civility",
  "phoneNumber",
  "specialtyCodes",
  "facilityName",
  "city",
  "state",
];

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    CSV_HEADERS,
    [
      "Jean",
      "Dupont",
      "MAN",
      "+221770000001",
      "FAMILY_MEDICINE",
      "Hôpital de Fann",
      "Dakar",
      "Dakar",
    ],
    ["Fatou", "Diallo", "WOMAN", "221770000002", "PEDIATRICS", "", "", ""],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Static Doctors");
  XLSX.writeFile(wb, "static_doctor_profiles_template.xlsx");
}

function parseSpecialtyCodes(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map(code => code.trim())
    .filter(Boolean);
}

// SheetJS can hand back non-string cell values (e.g. a phone number column parsed as a
// JS number, silently dropping a leading "+"), so every field is coerced defensively —
// otherwise a bare .trim() call throws and the whole file is reported as unparseable.
function cellToString(value: unknown): string {
  return value === undefined || value === null ? "" : String(value).trim();
}

function parseRawRows(rows: Record<string, unknown>[]): ParsedRow[] {
  return rows
    .filter(r => Object.values(r).some(v => cellToString(v)))
    .map(r => ({
      firstName: cellToString(
        r["firstName"] ?? r["first_name"] ?? r["First Name"]
      ),
      lastName: cellToString(r["lastName"] ?? r["last_name"] ?? r["Last Name"]),
      civility: cellToString(r["civility"] ?? r["Civility"]).toUpperCase(),
      phoneNumber: cellToString(
        r["phoneNumber"] ?? r["phone_number"] ?? r["Phone Number"]
      ),
      specialtyCodes: parseSpecialtyCodes(
        cellToString(r["specialtyCodes"] ?? r["specialty_codes"])
      ),
      facilityName: cellToString(r["facilityName"] ?? r["facility_name"]),
      city: cellToString(r["city"]),
      state: cellToString(r["state"]),
    }));
}

// MED-1362: civility is optional; anything other than MAN or WOMAN is shown in red, and the
// import rejects it (INVALID_CIVILITY).
function isInvalidCivility(civility: string): boolean {
  return civility !== "" && civility !== "MAN" && civility !== "WOMAN";
}

function rowHasMissingFields(row: ParsedRow): boolean {
  return !row.firstName || !row.lastName || !row.phoneNumber;
}

/** The body both the dry run and the real import send. */
function toBulkPayload(
  rows: ParsedRow[]
): BulkStaticDoctorProfileImportRequest {
  return {
    rows: rows.map(row => ({
      firstName: row.firstName,
      lastName: row.lastName,
      civility: row.civility || undefined,
      phoneNumber: row.phoneNumber,
      specialtyCodes: row.specialtyCodes,
      facilityName: row.facilityName || undefined,
      city: row.city || undefined,
      state: row.state || undefined,
    })),
  };
}

export const StaticDoctorDirectoryManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // MAN and WOMAN in the admin's language; any other value as typed; a dash when empty.
  const civilityLabel = (civility?: string) =>
    civility === "MAN" || civility === "WOMAN"
      ? t(`admin.staticDoctorProfiles.civility.${civility}`)
      : civility || "—";

  const rowErrorMessage = (e: BulkRowError) => {
    if (e.errorCode === "SPECIALTY_NOT_FOUND") {
      return t("admin.staticDoctorProfiles.bulkImport.specialtyNotFound", {
        row: e.row,
        value: e.value ?? "",
      });
    }
    if (e.errorCode === "INVALID_CIVILITY") {
      return t("admin.staticDoctorProfiles.bulkImport.invalidCivility", {
        row: e.row,
        value: e.value ?? "",
      });
    }
    return t("admin.staticDoctorProfiles.bulkImport.phoneDuplicate", {
      row: e.row,
      value: e.phoneNumber ?? "",
    });
  };

  const profiles = useAppSelector(selectStaticDoctorProfiles);
  const loading = useAppSelector(selectStaticDoctorProfilesLoading);
  const bulkImportLoading = useAppSelector(selectBulkImportLoading);
  const inviteLoadingId = useAppSelector(selectInviteLoadingId);
  const staticProfileError = useAppSelector(selectStaticDoctorProfilesError);
  const resendLoading = useAppSelector(selectResendLoading);
  const cancelLoading = useAppSelector(selectCancelLoading);
  const invitationError = useAppSelector(selectInvitationsError);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  // MED-1474: the dry run of the parsed file. `null` means "not checked yet".
  const [validationErrors, setValidationErrors] = useState<
    BulkRowError[] | null
  >(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationUnavailable, setValidationUnavailable] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [resendingId, setResendingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTarget, setCancelTarget] =
    useState<StaticDoctorProfileDto | null>(null);

  const prevBulkLoadingRef = useRef(false);
  const prevResendLoadingRef = useRef(false);
  const prevCancelLoadingRef = useRef(false);

  useEffect(() => {
    dispatch(fetchStaticDoctorProfilesRequest());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearStaticDoctorProfileError());
      dispatch(clearInvitationError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (prevBulkLoadingRef.current && !bulkImportLoading) {
      if (staticProfileError) {
        if (
          staticProfileError.code !==
          "STATIC_DOCTOR_PROFILE_BULK_VALIDATION_FAILED"
        ) {
          toast.error(t("admin.staticDoctorProfiles.toast.bulkImportError"));
        }
      } else {
        toast.success(t("admin.staticDoctorProfiles.toast.bulkImportSuccess"));
        setShowBulkModal(false);
        setParsedRows([]);
        setParseError(null);
      }
    }
    prevBulkLoadingRef.current = bulkImportLoading;
  }, [bulkImportLoading, staticProfileError, t]);

  useEffect(() => {
    if (prevResendLoadingRef.current && !resendLoading) {
      if (invitationError) {
        toast.error(t("admin.staticDoctorProfiles.toast.resendError"));
      } else if (resendingId !== null) {
        toast.success(t("admin.staticDoctorProfiles.toast.resendSuccess"));
      }
      setResendingId(null);
    }
    prevResendLoadingRef.current = resendLoading;
  }, [resendLoading, invitationError, resendingId, t]);

  useEffect(() => {
    if (prevCancelLoadingRef.current && !cancelLoading) {
      if (invitationError) {
        toast.error(t("admin.staticDoctorProfiles.toast.cancelError"));
      } else if (cancellingId !== null) {
        toast.success(t("admin.staticDoctorProfiles.toast.cancelSuccess"));
        // The invitation is gone; refresh so the row shows back as Static/ACTIVE.
        dispatch(fetchStaticDoctorProfilesRequest());
      }
      setCancellingId(null);
    }
    prevCancelLoadingRef.current = cancelLoading;
  }, [cancelLoading, invitationError, cancellingId, dispatch, t]);

  const filteredProfiles = profiles.filter(profile => {
    const fullName =
      `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    return (
      fullName.includes(term) ||
      (profile.phoneNumber ?? "").toLowerCase().includes(term)
    );
  });

  // GET /doctors/static-profiles has no server-side pagination — it returns the whole
  // directory in one response — so this paginates the already-fetched list client-side.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const totalItems = filteredProfiles.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedProfiles = filteredProfiles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const processFile = useCallback(
    (file: File) => {
      setParseError(null);
      setParsedRows([]);
      setValidationErrors(null);
      setValidationUnavailable(false);
      const name = file.name.toLowerCase();
      const isCsv = name.endsWith(".csv");
      const isExcel = name.endsWith(".xlsx") || name.endsWith(".xls");
      if (isCsv || isExcel) {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            // raw:true (on read(), not sheet_to_json) stops SheetJS from inferring cell
            // types from text content — without it a phone number like "+221770000001"
            // gets silently parsed as a JS number, dropping the "+". CSV is additionally
            // read as UTF-8 text (not a raw array buffer) so accented names/facilities
            // decode correctly instead of turning into mojibake.
            const wb = isCsv
              ? XLSX.read(e.target?.result as string, {
                  type: "string",
                  raw: true,
                })
              : XLSX.read(e.target?.result, { type: "array", raw: true });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
              defval: "",
            });
            setParsedRows(parseRawRows(rows));
          } catch {
            setParseError(
              t("admin.staticDoctorProfiles.bulkImport.parseError")
            );
          }
        };
        if (isCsv) {
          reader.readAsText(file, "utf-8");
        } else {
          reader.readAsArrayBuffer(file);
        }
      } else {
        setParseError(
          t("admin.staticDoctorProfiles.bulkImport.unsupportedFormat")
        );
      }
    },
    [t]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // MED-1474: dry-run the parsed file before the Importer button is enabled. An unknown
  // specialty code or an already-used phone number used to surface only after submitting,
  // row by row; the endpoint always answers 200 with the very errors the import would raise.
  useEffect(() => {
    if (parsedRows.length === 0) {
      setValidationErrors(null);
      setValidationUnavailable(false);
      return;
    }
    let cancelled = false;
    setIsValidating(true);
    setValidationUnavailable(false);
    staticDoctorProfileApi
      .validateBulkImport(toBulkPayload(parsedRows))
      .then(result => {
        if (!cancelled) {
          setValidationErrors(result.errors ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Unknown state: block the import rather than let it through unchecked.
          setValidationErrors(null);
          setValidationUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsValidating(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [parsedRows]);

  const hasMissingFields = parsedRows.some(rowHasMissingFields);
  const hasValidationErrors = (validationErrors?.length ?? 0) > 0;
  // MED-1474: a row the dry run rejected (unknown specialty code, duplicate
  // phone) was only named in the error list under the table — the row itself
  // was not highlighted, unlike a row with a missing field. `BulkRowError.row`
  // is the API's 1-based row number (`rowNum = i + 1`).
  const rowsInError = new Set(
    (validationErrors ?? []).map(e => e.row).filter(row => row > 0)
  );
  const importBlocked =
    parsedRows.length === 0 ||
    isValidating ||
    hasMissingFields ||
    hasValidationErrors ||
    validationUnavailable;

  const handleBulkSubmit = () => {
    if (importBlocked) return;
    dispatch(bulkImportStaticDoctorProfilesRequest(toBulkPayload(parsedRows)));
  };

  const handleInvite = (profile: StaticDoctorProfileDto) => {
    dispatch(inviteFromProfileRequest(profile.id));
  };

  const handleResend = (profile: StaticDoctorProfileDto) => {
    if (!profile.doctorInvitationId) return;
    setResendingId(profile.id);
    dispatch(resendInvitationRequest(profile.doctorInvitationId));
  };

  const handleCancelClick = (profile: StaticDoctorProfileDto) => {
    setCancelTarget(profile);
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    if (!cancelTarget?.doctorInvitationId) return;
    setCancellingId(cancelTarget.id);
    dispatch(cancelInvitationRequest(cancelTarget.doctorInvitationId));
    setShowCancelConfirm(false);
    setCancelTarget(null);
  };

  return (
    <div className="page-container">
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.staticDoctorProfiles.title")}
          </h2>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowBulkModal(true)}
          className="flex items-center space-x-2"
        >
          <Users className="w-4 h-4" />
          <span>{t("admin.staticDoctorProfiles.bulkImport.action")}</span>
        </Button>
      </div>

      <div className="page-section">
        <Input
          placeholder={t("common.search")}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />
      </div>

      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <p className="text-sm text-neutral-500">{t("common.loading")}</p>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.staticDoctorProfiles.noProfiles")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.staticDoctorProfiles.noProfilesDescription")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.civility")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.phone")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.facility")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.city")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.region")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.staticDoctorProfiles.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {paginatedProfiles.map(profile => (
                  <tr key={profile.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-neutral-600">
                            {profile.firstName?.[0]?.toUpperCase() ?? "?"}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-neutral-900">
                          {[profile.firstName, profile.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {civilityLabel(profile.civility)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {profile.phoneNumber
                        ? formatPhoneNumberForDisplay(profile.phoneNumber)
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={STATUS_BADGE_CLASS[profile.status]}>
                        {t(
                          `admin.staticDoctorProfiles.status.${profile.status}`
                        )}
                      </span>
                      {profile.status === "REPLACED" &&
                        profile.replacedByDoctorId && (
                          <Link
                            to={`/admin/doctors/${profile.replacedByDoctorId}`}
                            className="ml-2 text-xs text-ews-primary underline"
                          >
                            {t(
                              "admin.staticDoctorProfiles.table.viewRealProfile"
                            )}
                          </Link>
                        )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {profile.healthcareFacilities?.[0]?.name ??
                        profile.hospitalName ??
                        "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {profile.city ?? "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {profile.state ?? "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {profile.status === "ACTIVE" && (
                          <Button
                            variant="outline"
                            onClick={() => handleInvite(profile)}
                            disabled={inviteLoadingId === profile.id}
                            loading={inviteLoadingId === profile.id}
                          >
                            {t("admin.staticDoctorProfiles.table.invite")}
                          </Button>
                        )}
                        {profile.status === "INVITED" && (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => handleCancelClick(profile)}
                              disabled={cancellingId === profile.id}
                              loading={cancellingId === profile.id}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {t("admin.staticDoctorProfiles.table.cancel")}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleResend(profile)}
                              disabled={resendingId === profile.id}
                              loading={resendingId === profile.id}
                            >
                              {t("admin.staticDoctorProfiles.table.resend")}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              showPageNumbers={true}
              maxVisiblePages={5}
              showItemsPerPageSelector={true}
            />
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setCancelTarget(null);
        }}
        onConfirm={handleCancelConfirm}
        title={t("admin.staticDoctorProfiles.cancelConfirm.title")}
        message={t("admin.staticDoctorProfiles.cancelConfirm.message")}
        confirmLabel={t("admin.staticDoctorProfiles.cancelConfirm.confirm")}
        variant="danger"
        isLoading={cancelLoading}
      />

      {showBulkModal && (
        <Modal
          isOpen={showBulkModal}
          onClose={() => {
            setShowBulkModal(false);
            setParsedRows([]);
            setParseError(null);
          }}
          title={t("admin.staticDoctorProfiles.bulkImport.title")}
        >
          <div className="flex flex-col max-h-[80vh]">
            <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
              <div className="flex items-start justify-between gap-4 rounded-lg bg-blue-50 border border-blue-100 p-4">
                <div className="text-sm text-blue-800 space-y-1">
                  <p className="font-medium">
                    {t("admin.staticDoctorProfiles.bulkImport.instructions")}
                  </p>
                  <p className="text-xs text-blue-600">
                    {t("admin.staticDoctorProfiles.bulkImport.columns")}:{" "}
                    <code className="font-mono">
                      firstName, lastName, civility, phoneNumber,
                      specialtyCodes, facilityName, city, state
                    </code>
                  </p>
                  <p className="text-xs text-blue-600">
                    {t("admin.staticDoctorProfiles.bulkImport.civilityHelp")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="shrink-0 text-xs font-medium text-blue-700 underline hover:text-blue-900 whitespace-nowrap"
                >
                  {t("admin.staticDoctorProfiles.bulkImport.downloadTemplate")}
                </button>
              </div>

              {parsedRows.length === 0 && (
                <div
                  onDragOver={e => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 cursor-pointer transition-colors ${
                    isDragging
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
                  }`}
                >
                  <Users className="w-8 h-8 text-gray-400" />
                  <p className="text-sm font-medium text-gray-600">
                    {t("admin.staticDoctorProfiles.bulkImport.dropZone")}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t("admin.staticDoctorProfiles.bulkImport.acceptedFormats")}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {parseError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {parseError}
                </div>
              )}

              {parsedRows.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      {t("admin.staticDoctorProfiles.bulkImport.preview", {
                        count: parsedRows.length,
                      })}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setParsedRows([]);
                        setParseError(null);
                      }}
                      className="text-xs text-gray-500 underline hover:text-gray-700"
                    >
                      {t("admin.staticDoctorProfiles.bulkImport.changeFile")}
                    </button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            #
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.form.firstName")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.form.lastName")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.form.civility")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.form.phoneNumber")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t(
                              "admin.staticDoctorProfiles.form.specialtyCodes"
                            )}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.form.facilityName")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.table.city")}
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            {t("admin.staticDoctorProfiles.table.region")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {parsedRows.map((row, i) => (
                          <tr
                            key={i}
                            className={
                              rowHasMissingFields(row) || rowsInError.has(i + 1)
                                ? "bg-red-50"
                                : ""
                            }
                          >
                            <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                            <td
                              className={`px-3 py-2 ${!row.firstName ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {row.firstName || "—"}
                            </td>
                            <td
                              className={`px-3 py-2 ${!row.lastName ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {row.lastName || "—"}
                            </td>
                            <td
                              className={`px-3 py-2 ${isInvalidCivility(row.civility) ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {civilityLabel(row.civility)}
                            </td>
                            <td
                              className={`px-3 py-2 ${!row.phoneNumber ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {row.phoneNumber
                                ? formatPhoneNumberForDisplay(row.phoneNumber)
                                : "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.specialtyCodes.join(", ") || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.facilityName || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.city || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.state || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {hasMissingFields && (
                    <p className="text-xs text-red-600">
                      {t("admin.staticDoctorProfiles.bulkImport.rowErrors")}
                    </p>
                  )}

                  {isValidating && (
                    <p className="text-xs text-gray-500">
                      {t("admin.staticDoctorProfiles.bulkImport.validating")}
                    </p>
                  )}

                  {validationUnavailable && (
                    <p className="text-xs text-red-600">
                      {t(
                        "admin.staticDoctorProfiles.bulkImport.validationUnavailable"
                      )}
                    </p>
                  )}

                  {/* The dry run returns the same BulkRowError objects as the real
                      import's 400 body, so the same renderer covers both. */}
                  {hasValidationErrors && (
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <p className="mb-2 text-sm font-medium text-red-700">
                        {t(
                          "admin.staticDoctorProfiles.bulkImport.previewErrors"
                        )}
                      </p>
                      <ul className="space-y-1 text-sm text-red-600">
                        {(validationErrors ?? []).map((e, i) => (
                          <li key={i}>{rowErrorMessage(e)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {staticProfileError &&
                staticProfileError.code ===
                  "STATIC_DOCTOR_PROFILE_BULK_VALIDATION_FAILED" &&
                Array.isArray(staticProfileError.data) && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm font-medium text-red-700 mb-2">
                      {t(
                        "admin.staticDoctorProfiles.bulkImport.bulkValidationFailed"
                      )}
                    </p>
                    <ul className="text-sm text-red-600 space-y-1">
                      {staticProfileError.data.map(
                        (e: BulkRowError, i: number) => (
                          <li key={i}>{rowErrorMessage(e)}</li>
                        )
                      )}
                    </ul>
                  </div>
                )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkModal(false);
                  setParsedRows([]);
                  setParseError(null);
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleBulkSubmit}
                loading={bulkImportLoading}
                disabled={bulkImportLoading || importBlocked}
              >
                {t("admin.staticDoctorProfiles.bulkImport.submit", {
                  count: parsedRows.length,
                })}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
